import React, {
    BaseSyntheticEvent,
    FormEvent,
    useState,
    useEffect,
} from 'react';
import { IStackTokens, ITextStyles, Stack, Text } from '@fluentui/react';
import { Checkbox } from '../components/common/checkbox';
import { InternalLinkSection } from '../components/internalLinkSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useFormValidationDialog } from '../helpers/hooks';
import { useAppDispatch, useAppSelector } from '../store';
import { ProgressDialog, WarningDialog } from '../components/dialogs';
import { logger } from '../helpers';
import { appActions } from '../store/actions/app/actions';
import {
    getIntialEmailShareValues,
    ShareByEmailData,
} from '../form-loaders/shareByEmail';
import {
    shareByEmailScehma,
    shareWithCirclesSchema,
    shareWithMembersSchema,
} from '../schemas/shareByEmail';
// eslint-disable-next-line max-len
import { ControlledTextField } from '../components/form/controls/controlledTextField';
import { SharePermissions } from '@sendent/nextcloud-api';
import { selectTranslate } from '../store/selectors/ui';
import { WizardFooter } from '../components/wizard/footer';
import {
    shareServerFileByEmailThunk,
    shareServerFileWithMembersThunk,
} from '../store/actions/app/thunks';
import {
    emailShareDndFilesThunk,
    shareDndFilesWithCircleThunk,
    shareDndFilesWithMembersThunk,
} from '../store/actions/dnd/thunks';
import { selectDestinationFolderPath } from '../store/selectors/app';
import {
    setProgressSize,
    showNotification,
} from '../store/actions/app/actionCreators';
import {
    AppErrorCode,
    NotificationStatus,
} from '../store/actions/app/actionTypes';
import { isLocalFile, isServerFile } from '../model';
import { chunkedUploadAbortControllerRefBox } from '../services/file';
import { selectTeamsPathUploadFiles } from '../store/selectors/auth';
import {
    selectChatMemberCount,
    selectMemberResolution,
    selectTeamsContext,
} from '../store/selectors/conversation';
import { resolveChatMembersThunk } from '../store/actions/conversation/thunks';
import { MemberResolutionStatus } from '../store/reducers/conversation/reducer';
import { ChatMemberSelector } from '../components/common/chatMemberSelector';
import {
    getInitialMemberShareValues,
    ShareWithMembersData,
} from '../form-loaders/shareWithMembers';
import {
    Circle,
    isGroupChat as checkIsGroupChat,
} from '../services/microsoft/msGraphApi';
import { selectCircles } from '../store/selectors/nextcloud';
import { shareSelectedFileWithCircleThunk } from '../store/actions/nextcloud/thunks';
import { DestinationFolder } from '../components/wizard/steps/dndFiles';
import { CircleSelector } from '../components/common/CircleSelector';
import {
    getIntialCircleShareValues,
    ShareWithCircleData,
} from '../form-loaders/shareWithCircle';

const containerTokens: IStackTokens = { childrenGap: 20 };

const pageDescriptionTextStyles: ITextStyles = {
    root: { lineHeight: 20, color: '#98908C' },
};

const emailContainerTokens: IStackTokens = { childrenGap: 8 };

export enum ShareTypePageVariant {
    Regular = 'regular',
    Dnd = 'dnd',
}

export type OrganizationSharePageProps = {
    variant: ShareTypePageVariant;
};

export const OrganizationSharePage = ({
    variant,
}: OrganizationSharePageProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const translate = useAppSelector(selectTranslate);
    const destinationFolderPath = useAppSelector(selectDestinationFolderPath);
    const defaultUploadFolder = useAppSelector(selectTeamsPathUploadFiles);
    const teamsContext = useAppSelector(selectTeamsContext);
    const circles = useAppSelector(selectCircles);

    const chatMemberCount = useAppSelector(selectChatMemberCount);
    const isGroupChat = checkIsGroupChat(chatMemberCount);
    const memberResolution = useAppSelector(selectMemberResolution);

    const [selectedCircle, setSelectedCircle] = useState<Circle[]>([]);
    const isChannelOrTeam = Boolean(
        teamsContext?.channel?.id || teamsContext?.team?.groupId,
    );
    const getInitialFormValues = () => {
        if (isChannelOrTeam) {
            return getIntialCircleShareValues();
        }
        if (isGroupChat) {
            return getInitialMemberShareValues();
        }
        return getIntialEmailShareValues();
    };

    const getFormSchema = () => {
        if (isChannelOrTeam) {
            return shareWithCirclesSchema;
        }
        if (isGroupChat) {
            return shareWithMembersSchema;
        }
        return shareByEmailScehma;
    };

    const initialFormValues = getInitialFormValues();
    const preselectedCircle = (initialFormValues as ShareWithCircleData)
        .preselectedCircle;

    useEffect(() => {
        if (preselectedCircle) {
            setSelectedCircle([preselectedCircle]);
        }
    }, [preselectedCircle]);

    const {
        watch,
        register,
        setValue,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<ShareByEmailData | ShareWithCircleData | ShareWithMembersData>({
        defaultValues: initialFormValues,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        resolver: zodResolver(getFormSchema()),
        criteriaMode: 'firstError',
    });

    const { errorToDisplay, showValidationDialog, setShowValidationDialog } =
        useFormValidationDialog(errors);

    useEffect(() => {
        if (!isGroupChat || isChannelOrTeam) {
            return;
        }
        dispatch(resolveChatMembersThunk()).then((resolution) => {
            if (resolution.status === MemberResolutionStatus.Done) {
                setValue(
                    'selectedMembers',
                    resolution.members.filter((member) => member.ncShareWith),
                );
            }
        });
    }, [dispatch, isGroupChat, isChannelOrTeam, setValue]);

    const [progressDialogHidden, setProgressDialogHidden] = useState(true);

    const onUpload = async (
        data: Partial<
            ShareByEmailData & ShareWithMembersData & ShareWithCircleData
        >,
        e?: BaseSyntheticEvent,
    ) => {
        e?.preventDefault();
        logger.info('onUpload called', 'Share');

        const { files, email, selectedMembers } = data;
        // the form schemas validate files before submit can fire
        if (!files) return;
        // every form-loader initialises permissions with Read
        const permissions = data.permissions ?? SharePermissions.Read;

        if (variant === ShareTypePageVariant.Regular) {
            const file = files[0];
            // the regular flow shares a file that was already uploaded
            if (!file || !isServerFile(file)) {
                logger.error('onUpload: no uploaded server file', 'Share');
                dispatch(
                    showNotification({
                        errorCode: AppErrorCode.CustomError,
                        message: translate('something_went_wrong'),
                        status: NotificationStatus.Error,
                    }),
                );
                return;
            }
            if (isChannelOrTeam) {
                dispatch(
                    shareSelectedFileWithCircleThunk(file, selectedCircle[0]),
                );
                return;
            }
            if (isGroupChat) {
                dispatch(
                    shareServerFileWithMembersThunk(
                        file,
                        selectedMembers ?? [],
                        permissions,
                    ),
                );
                return;
            }
            if (email) {
                dispatch(shareServerFileByEmailThunk(email, file, permissions));
                return;
            }

            dispatch(shareSelectedFileWithCircleThunk(file, selectedCircle[0]));
            return;
        }

        const uploadFolder: DestinationFolder = destinationFolderPath
            ? destinationFolderPath
            : defaultUploadFolder;

        setProgressDialogHidden(false);
        dispatch(setProgressSize(0));
        const onUploadProgress = (totalUploadedBytes: number) =>
            dispatch(setProgressSize(totalUploadedBytes));

        if (variant === ShareTypePageVariant.Dnd) {
            // the dnd flow only ever stages local picks
            const localFiles = files.filter(isLocalFile);
            if (isGroupChat && !isChannelOrTeam) {
                await dispatch(
                    shareDndFilesWithMembersThunk({
                        files: localFiles,
                        folder: uploadFolder,
                        members: selectedMembers ?? [],
                        permissions,
                        onUploadProgress,
                    }),
                );
                return;
            }
            if (email) {
                await dispatch(
                    emailShareDndFilesThunk({
                        email,
                        files: localFiles,
                        folder: uploadFolder,
                        permissions,
                        onUploadProgress,
                    }),
                );
                return;
            }
            if (isChannelOrTeam) {
                await dispatch(
                    shareDndFilesWithCircleThunk({
                        files: localFiles,
                        folder: uploadFolder,
                        selectedCircle: selectedCircle[0],
                        onUploadProgress,
                    }),
                );
                return;
            }
        }
    };

    const cancelUploadAndHideDialog = (isHidden: boolean) => {
        chunkedUploadAbortControllerRefBox
            .getValue()
            .abort('User cancelled upload');
        setProgressDialogHidden(isHidden);
    };

    const hideValidationDialog = () => setShowValidationDialog(false);

    const permissionsGranted = watch('permissions') === SharePermissions.Update;
    const isDropdownDisabled = watch('isDropdownDisabled');
    const selectedMembers = watch('selectedMembers') ?? [];

    const onPermissionChange = (
        _e?: FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean,
    ): void => {
        const permissions = checked
            ? SharePermissions.Update
            : SharePermissions.Read;

        setValue('permissions', permissions);
    };

    const onInternalLinkClick = () =>
        dispatch(appActions.shareLink(watch('files')));

    return (
        <form onSubmit={handleSubmit(onUpload)}>
            <Stack tokens={containerTokens}>
                <Text variant="medium" styles={pageDescriptionTextStyles}>
                    {translate('organization_share_description')}
                </Text>
                <Stack tokens={emailContainerTokens}>
                    {isChannelOrTeam ? (
                        <Stack tokens={{ childrenGap: 8 }}>
                            <CircleSelector
                                circles={circles ?? []}
                                selectedCircleIds={selectedCircle.map(
                                    (circle) => circle.id,
                                )}
                                onSelectionChange={setSelectedCircle}
                                searchPlaceholder={translate('search')}
                                dropdownPlaceholder={
                                    preselectedCircle?.name ||
                                    translate('no_team_selected')
                                }
                                isMultiSelect={false}
                                isDropdownDisabled={isDropdownDisabled}
                            />
                        </Stack>
                    ) : isGroupChat ? (
                        <ChatMemberSelector
                            members={memberResolution.members}
                            status={memberResolution.status}
                            selectedUserIds={selectedMembers.map(
                                (member) => member.userId,
                            )}
                            onSelectionChange={(members) =>
                                setValue('selectedMembers', members)
                            }
                        />
                    ) : (
                        <ControlledTextField
                            label={translate('feedback_contactemail')}
                            control={control}
                            {...register('email')}
                        />
                    )}
                    <Checkbox
                        color="var(--sdt-primary)"
                        checked={isChannelOrTeam || permissionsGranted}
                        disabled={isChannelOrTeam}
                        label={translate('grant_update_permissions')}
                        onChange={onPermissionChange}
                    />
                </Stack>
                {variant === ShareTypePageVariant.Regular && (
                    <InternalLinkSection onClick={onInternalLinkClick} />
                )}
            </Stack>
            <WizardFooter />
            <WarningDialog
                hidden={!showValidationDialog}
                subText={errorToDisplay?.message}
                icon="ErrorBadge"
                color="red"
                onDismiss={hideValidationDialog}
            />
            {variant === ShareTypePageVariant.Dnd && (
                <ProgressDialog
                    hidden={progressDialogHidden}
                    onDismiss={cancelUploadAndHideDialog}
                    title={translate('progress_header')}
                    subText={translate('progress_count')}
                />
            )}
        </form>
    );
};
